/**
 * Promotions Store
 * Manages promotions (sales) data for the current merakez/assortment
 * Loaded during catalog bootstrap and kept independent for later item mapping
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getFirebaseDb } from '../../core/firebase/firebase.js'
import { collection, query, where, getDocs } from 'firebase/firestore'
import logger from '../../core/services/logger.js'
import { useAuthStore } from './auth.js'
import {
  promoContainsMakat,
  filterEligiblePromos,
  sortPromosByPriority,
  isMadregotPromo,
  getPromoMakats,
} from '../../core/utils/promoCalc.js'

const log = logger.createScoped('PromotionsStore')

/**
 * Parse date safely from various formats
 * @param {any} dateValue - Firestore Timestamp, string date, or null
 * @returns {Date|null}
 */
function parseDate(dateValue) {
  if (!dateValue) return null

  // Firestore Timestamp
  if (dateValue.toDate && typeof dateValue.toDate === 'function') {
    return dateValue.toDate()
  }

  // String date (YYYY-MM-DD)
  if (typeof dateValue === 'string') {
    const parsed = new Date(dateValue)
    if (!isNaN(parsed.getTime())) {
      return parsed
    }
  }

  // Already a Date
  if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
    return dateValue
  }

  return null
}

/**
 * Check if a promotion is currently valid (date-wise)
 * @param {Object} promotion
 * @returns {boolean}
 */
function isDateValid(promotion) {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  // Try timestamp fields first, fallback to string fields
  const startDate = parseDate(promotion.date_start_date) || parseDate(promotion.start_date)
  const endDate = parseDate(promotion.date_end_date) || parseDate(promotion.end_date)

  // If no dates, consider it valid (no date restrictions)
  if (!startDate && !endDate) {
    return true
  }

  // Check start date
  if (startDate) {
    const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
    if (today < startDay) {
      return false
    }
  }

  // Check end date
  if (endDate) {
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
    if (today > endDay) {
      return false
    }
  }

  return true
}

/**
 * Normalize a promotion document to consistent shape
 * @param {Object} doc - Firestore document snapshot
 * @returns {Object}
 */
function normalizePromotion(doc) {
  const data = doc.data()

  return {
    // Core identifiers
    id: doc.id,
    uid: data.uid || doc.id,

    // Merakez/assortment
    merakez: data.merakez,

    // Status flags
    active: data.active ?? false,
    approved: data.approved ?? false,
    first_order: data.first_order ?? false,
    weeklyBenefit: data.weeklyBenefit ?? false,

    // Display fields
    label: data.label || '',
    description: data.description || '',
    saleType: data.saleType || null,
    image: data.image || null,

    // Date fields (keep both formats)
    start_date: data.start_date || null,
    end_date: data.end_date || null,
    date_start_date: data.date_start_date || null,
    date_end_date: data.date_end_date || null,

    // Items - PRIMARY source for makats
    items: data.items || [],
    makats_arr: data.makats_arr || [],

    // Steps (for madregot type)
    step_1: data.step_1 || null,
    step_2: data.step_2 || null,
    step_3: data.step_3 || null,
    step_4: data.step_4 || null,
    step_5: data.step_5 || null,

    // Max quantity
    max: data.max || null,

    // Preserve all other fields
    ...data,

    // Ensure our normalized fields take precedence
    id: doc.id,
    uid: data.uid || doc.id,
  }
}

export const usePromotionsStore = defineStore('promotions', () => {
  // ============================================
  // State
  // ============================================
  const isLoading = ref(false)
  const error = ref(null)
  const merakez = ref(null)
  const items = ref([])
  const byId = ref(new Map())
  const lastLoadedAt = ref(null)

  // Linked client (set by catalog store to avoid circular dependency)
  const _linkedClient = ref(null)

  // ============================================
  // Getters
  // ============================================

  /**
   * Total count of loaded promotions
   */
  const count = computed(() => items.value.length)

  /**
   * Promotions for new members only (first_order)
   */
  const firstOrderPromotions = computed(() => {
    return items.value.filter((p) => p.first_order === true)
  })

  /**
   * Regular promotions (not first_order)
   */
  const regularPromotions = computed(() => {
    return items.value.filter((p) => p.first_order !== true)
  })

  /**
   * Get promotion by ID
   */
  function getById(id) {
    return byId.value.get(id) || null
  }

  /**
   * Get promotions that include a specific makat
   * Uses promotion.items[].makat as primary source
   */
  function getPromotionsForMakat(makat) {
    if (!makat) return []
    return items.value.filter((p) => promoContainsMakat(p, makat))
  }

  /**
   * Check if current user is eligible for first_order promotions
   * Checks both auth client (regular users) and catalog linkedClient (agent mode)
   * @returns {boolean}
   */
  function isFirstOrderEligible() {
    try {
      // Check 1: Regular client user
      const authStore = useAuthStore()
      if (authStore.user?.client?.first_order === true) return true

      // Check 2: Agent viewing a client's catalog (set via setLinkedClient)
      if (_linkedClient.value?.first_order === true) return true

      return false
    } catch {
      return false
    }
  }

  /**
   * Set linked client for first_order eligibility (called by catalog store)
   * Avoids circular dependency between catalog and promotions stores
   * @param {Object|null} client
   */
  function setLinkedClient(client) {
    _linkedClient.value = client
  }

  /**
   * Get all eligible active promos in priority order
   * Priority: end_date ASC, then id ASC
   * Filters by first_order eligibility
   * When user is first_order eligible: regular promos that share ANY makat
   * with a first_order promo are excluded (first_order promos have better prices)
   * @returns {Array}
   */
  function getEligibleActivePromos() {
    const eligible = isFirstOrderEligible()
    const filtered = filterEligiblePromos(items.value, eligible)

    // When user is first_order eligible, remove regular promos that overlap
    // with any first_order promo (even a single shared makat is enough)
    if (eligible) {
      const firstOrderMakats = new Set()
      for (const promo of filtered) {
        if (promo.first_order === true) {
          for (const m of getPromoMakats(promo)) {
            firstOrderMakats.add(m)
          }
        }
      }

      if (firstOrderMakats.size > 0) {
        const withoutOverlap = filtered.filter((promo) => {
          if (promo.first_order === true) return true
          const makats = getPromoMakats(promo)
          return !makats.some((m) => firstOrderMakats.has(m))
        })
        return sortPromosByPriority(withoutOverlap)
      }
    }

    return sortPromosByPriority(filtered)
  }

  /**
   * Get eligible promos containing a specific makat, in priority order
   * @param {string} makat
   * @returns {Array}
   */
  function getEligiblePromosForMakat(makat) {
    if (!makat) return []
    const allPromos = getEligibleActivePromos()
    return allPromos.filter((p) => promoContainsMakat(p, makat))
  }

  /**
   * Get multi-item promotions (2+ items)
   * Used for consolidated promotion cards
   * @returns {Array}
   */
  function getMultiItemPromotions() {
    const eligible = getEligibleActivePromos()
    return eligible.filter((p) => {
      const makats = getPromoMakats(p)
      return makats.length >= 2
    })
  }

  /**
   * Get single-item promotions
   * These render as regular item cards
   * @returns {Array}
   */
  function getSingleItemPromotions() {
    const eligible = getEligibleActivePromos()
    return eligible.filter((p) => {
      const makats = getPromoMakats(p)
      return makats.length === 1
    })
  }

  /**
   * Get makats that are ONLY in single-item promotions
   * (not in any multi-item promotion)
   * @returns {string[]}
   */
  function getSinglePromoMakats() {
    const multiPromos = getMultiItemPromotions()
    const multiMakats = new Set()
    multiPromos.forEach((p) => {
      getPromoMakats(p).forEach((m) => multiMakats.add(m))
    })

    const singlePromos = getSingleItemPromotions()
    const singleMakats = []
    singlePromos.forEach((p) => {
      const makat = getPromoMakats(p)[0]
      if (makat && !multiMakats.has(makat)) {
        singleMakats.push(makat)
      }
    })
    return singleMakats
  }

  // ============================================
  // Actions
  // ============================================

  /**
   * Reset store to initial state
   */
  function reset() {
    isLoading.value = false
    error.value = null
    merakez.value = null
    items.value = []
    byId.value = new Map()
    lastLoadedAt.value = null
    _linkedClient.value = null
    log.info('Promotions store reset')
  }

  /**
   * Set the current merakez (without loading)
   */
  function setMerakez(newMerakez) {
    merakez.value = newMerakez
  }

  /**
   * Load promotions for a specific merakez
   * Queries Sales collection with active + approved filters
   * Date filtering done client-side to avoid composite index requirements
   *
   * @param {string} targetMerakez - The merakez/assortment ID
   * @returns {Promise<{count: number}>}
   */
  async function loadPromotionsForMerakez(targetMerakez) {
    if (!targetMerakez) {
      log.warn('loadPromotionsForMerakez called without merakez')
      return { count: 0 }
    }

    // Skip if already loaded for this merakez
    if (merakez.value === targetMerakez && items.value.length > 0) {
      log.debug('Promotions already loaded for merakez', { merakez: targetMerakez })
      return { count: items.value.length }
    }

    isLoading.value = true
    error.value = null

    try {
      const db = getFirebaseDb()
      const salesRef = collection(db, 'Sales')

      // Query: merakez + active + approved
      const q = query(
        salesRef,
        where('merakez', '==', targetMerakez),
        where('active', '==', true),
        where('approved', '==', true)
      )

      log.info('Loading promotions for merakez', { merakez: targetMerakez })

      const snapshot = await getDocs(q)

      // Normalize and filter by date (client-side)
      const allPromotions = []
      snapshot.forEach((doc) => {
        const promotion = normalizePromotion(doc)
        if (isDateValid(promotion)) {
          allPromotions.push(promotion)
        }
      })

      // Sort by priority: end_date ASC, then id ASC (deterministic)
      allPromotions.sort((a, b) => {
        const endA = parseDate(a.date_end_date) || parseDate(a.end_date)
        const endB = parseDate(b.date_end_date) || parseDate(b.end_date)

        // No end date = sort to end (lasting indefinitely)
        if (!endA && !endB) {
          // Tie-break by id
          return String(a.id || '').localeCompare(String(b.id || ''))
        }
        if (!endA) return 1
        if (!endB) return -1

        const dateCompare = endA.getTime() - endB.getTime()
        if (dateCompare !== 0) return dateCompare

        // Tie-break by id
        return String(a.id || '').localeCompare(String(b.id || ''))
      })

      // Deduplicate promotions with identical makats + first_order flag
      // Admin may accidentally create multiple Sale docs for the same promotion
      const seen = new Set()
      const dedupedPromotions = allPromotions.filter((promo) => {
        const makats = getPromoMakats(promo)
        const key = `${promo.first_order ? '1' : '0'}:${makats.join(',')}`
        if (seen.has(key)) {
          log.debug('Skipping duplicate promotion', { id: promo.id, label: promo.label, key })
          return false
        }
        seen.add(key)
        return true
      })

      if (dedupedPromotions.length < allPromotions.length) {
        log.info('Deduplicated promotions', {
          before: allPromotions.length,
          after: dedupedPromotions.length,
          removed: allPromotions.length - dedupedPromotions.length,
        })
      }

      // Build byId map
      const idMap = new Map()
      for (const promo of dedupedPromotions) {
        idMap.set(promo.id, promo)
      }

      // Update state
      merakez.value = targetMerakez
      items.value = dedupedPromotions
      byId.value = idMap
      lastLoadedAt.value = new Date()

      const firstOrderCount = allPromotions.filter((p) => p.first_order).length
      const regularCount = allPromotions.filter((p) => !p.first_order).length

      log.info('Promotions loaded successfully', {
        merakez: targetMerakez,
        count: allPromotions.length,
        firstOrder: firstOrderCount,
        regular: regularCount,
      })

      return { count: allPromotions.length }
    } catch (err) {
      log.error('Failed to load promotions', { merakez: targetMerakez, error: err })
      error.value = err.message || 'Failed to load promotions'
      return { count: 0 }
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    isLoading,
    error,
    merakez,
    items,
    byId,
    lastLoadedAt,

    // Getters
    count,
    firstOrderPromotions,
    regularPromotions,

    // Methods
    getById,
    getPromotionsForMakat,
    isFirstOrderEligible,
    getEligibleActivePromos,
    getEligiblePromosForMakat,
    getMultiItemPromotions,
    getSingleItemPromotions,
    getSinglePromoMakats,

    // Actions
    reset,
    setMerakez,
    setLinkedClient,
    loadPromotionsForMerakez,
  }
})
